import { useEffect, useRef, useState } from 'react';
import api from '../utils/api';

/**
 * usePaymentPoller Hook
 * 
 * Handles polling for Paystack payment status with intelligent retry logic
 * and exponential backoff for network resilience
 * 
 * @param {string} transactionId - The transaction ID to poll
 * @param {object} options - Configuration options
 *   - pollingEndpoint: Optional endpoint pattern for polling (default: '/api/payments/{id}')
 *   - onSuccess, onFailure, onPolling: Callbacks
 *   - maxRetries, initialInterval, maxInterval, totalTimeout, backoffMultiplier: Retry config
 * @returns {object} - Current status, error, and control functions
 */
export const usePaymentPoller = (transactionId, options = {}) => {
  const {
    onSuccess = null,
    onFailure = null,
    onPolling = null,
    pollingEndpoint = '/api/payments/{id}', // Default polling endpoint pattern
    maxRetries = 5,
    initialInterval = 2000, // Start with 2 seconds
    maxInterval = 30000, // Cap at 30 seconds
    totalTimeout = 600000, // 10 minutes total
    backoffMultiplier = 1.5,
  } = options;

  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);

  const pollerRef = useRef(null);
  const startTimeRef = useRef(null);
  const retryCountRef = useRef(0);
  const isMountedRef = useRef(true);

  // Calculate next polling interval with exponential backoff
  const calculateNextInterval = (attempt) => {
    const interval = Math.min(
      initialInterval * Math.pow(backoffMultiplier, attempt),
      maxInterval
    );
    return Math.floor(interval);
  };

  // Build polling endpoint URL with transaction ID
  const getPollingEndpoint = () => {
    return pollingEndpoint.replace('{id}', transactionId);
  };

  // Fetch payment status from backend
  const fetchPaymentStatus = async () => {
    try {
      const endpoint = getPollingEndpoint();
      const response = await api.get(endpoint);
      
      if (!isMountedRef.current) return;

      retryCountRef.current = 0; // Reset retry count on success

      setStatus(response);
      setError(null);

      if (onPolling) {
        onPolling({
          status: response.status,
          message: response.message,
          amount: response.amount,
          reference: response.reference,
        });
      }

      // Handle final status
      if (response.status === 'success') {
        clearTimeout(pollerRef.current);
        setIsPolling(false);
        if (onSuccess) {
          onSuccess(response);
        }
        return true; // Signal completion
      } else if (response.status === 'failed') {
        clearTimeout(pollerRef.current);
        setIsPolling(false);
        setError(response.failed_reason || 'Payment failed');
        if (onFailure) {
          onFailure(response);
        }
        return true; // Signal completion
      }

      return false; // Continue polling
    } catch (err) {
      if (!isMountedRef.current) return;

      retryCountRef.current += 1;

      // Log error but continue polling
      console.warn(`Payment status check failed (attempt ${retryCountRef.current}/${maxRetries}):`, err.message);

      if (retryCountRef.current >= maxRetries) {
        setError(`Network error after ${maxRetries} retries. Please check your connection.`);
        if (onFailure) {
          onFailure({ error: err.message, retries: retryCountRef.current });
        }
        clearTimeout(pollerRef.current);
        setIsPolling(false);
        return true; // Stop polling
      }

      return false; // Continue polling with next interval
    }
  };

  // Start polling
  const startPolling = () => {
    if (!transactionId) {
      setError('No transaction ID provided');
      return;
    }

    if (isPolling) return;

    setIsPolling(true);
    setStatus(null);
    setError(null);
    setAttemptCount(0);
    retryCountRef.current = 0;
    startTimeRef.current = Date.now();

    const pollFn = async () => {
      // Check if total timeout exceeded
      if (Date.now() - startTimeRef.current > totalTimeout) {
        setError('Payment verification timeout. Attempting manual verification...');
        setIsPolling(false);
        
        // Try manual verification as fallback
        try {
          console.log('Polling timeout reached, attempting manual verification...');
          const verifyEndpoint = getPollingEndpoint().replace(/\/$/, '') + '/verify';
          const verifyResponse = await api.post(verifyEndpoint);
          
          if (verifyResponse.status === 'success') {
            console.log('Manual verification succeeded!');
            setStatus(verifyResponse);
            setError(null);
            if (onSuccess) {
              onSuccess(verifyResponse);
            }
            return;
          } else if (verifyResponse.status === 'failed') {
            console.log('Manual verification found failed payment');
            setError(verifyResponse.message || 'Payment verification failed');
            if (onFailure) {
              onFailure(verifyResponse);
            }
            return;
          } else {
            console.log('Manual verification still pending:', verifyResponse);
            setError('Payment still processing. Please try again in a moment.');
            if (onFailure) {
              onFailure(verifyResponse);
            }
            return;
          }
        } catch (err) {
          console.error('Manual verification failed:', err);
          setError('Payment verification timeout. Please check your payment status manually or contact support.');
          if (onFailure) {
            onFailure({ error: err.message, timeout: totalTimeout });
          }
          return;
        }
      }

      // Fetch status
      const isComplete = await fetchPaymentStatus();
      
      if (!isMountedRef.current) return;

      setAttemptCount(prev => prev + 1);

      if (!isComplete) {
        // Calculate next interval and schedule next check
        const nextInterval = calculateNextInterval(attemptCount);
        pollerRef.current = setTimeout(pollFn, nextInterval);
      }
    };

    // First check immediately
    pollFn();
  };

  // Stop polling
  const stopPolling = () => {
    if (pollerRef.current) {
      clearTimeout(pollerRef.current);
    }
    setIsPolling(false);
  };

  // Manual status check
  const checkStatus = async () => {
    await fetchPaymentStatus();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (pollerRef.current) {
        clearTimeout(pollerRef.current);
      }
    };
  }, []);

  return {
    status,
    error,
    isPolling,
    attemptCount,
    startPolling,
    stopPolling,
    checkStatus,
  };
};

export default usePaymentPoller;
